import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// LiquidGlass — Apple-style frosted-glass surface with an SVG distortion edge
//
// Adapted from suraj-xd / 21st.dev's Liquid Glass component for Helix's dark
// theme. The original assumed a light backdrop and used black text; here the
// default text color is white and the inner highlight + drop shadow are tuned
// for dark surfaces.
//
// Architecture — three stacked absolute layers + content:
//   z-0:  backdrop-blur(3px) + the SVG distortion filter (the "warp" edge)
//   z-10: 25% white wash (gives the frosted look regardless of backdrop)
//   z-20: inner highlight (catches light on the top-left corner like glass)
//   z-30: the actual children
//
// Usage:
//   <LiquidGlassFilter />   // Mount once near the root (it's an SVG <defs>)
//   <LiquidGlass>...</LiquidGlass>
//   <LiquidGlassButton href="...">Click me</LiquidGlassButton>
//
// Cubic bezier easing (0.175, 0.885, 0.32, 2.2) gives the springy hover feel
// from the source design. Don't change it without re-testing the hover bounce.
// ─────────────────────────────────────────────────────────────────────────────

const SPRING_EASE = 'cubic-bezier(0.175, 0.885, 0.32, 2.2)';

// Render this once high in the tree (e.g. inside the landing page root). It
// declares an SVG filter that LiquidGlass references via filter: url(#...).
// The filter creates the warped, refractive edge that makes the glass look
// "liquid" rather than just a frosted overlay.
export const LiquidGlassFilter = () => (
    <svg style={{ display: 'none' }} aria-hidden="true">
        <filter
            id="helix-glass-distortion"
            x="0%" y="0%" width="100%" height="100%"
            filterUnits="objectBoundingBox"
        >
            <feTurbulence
                type="fractalNoise"
                baseFrequency="0.001 0.005"
                numOctaves="1"
                seed="17"
                result="turbulence"
            />
            <feComponentTransfer in="turbulence" result="mapped">
                <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
                <feFuncG type="gamma" amplitude="0" exponent="1"  offset="0"   />
                <feFuncB type="gamma" amplitude="0" exponent="1"  offset="0.5" />
            </feComponentTransfer>
            <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
            <feSpecularLighting
                in="softMap"
                surfaceScale="5"
                specularConstant="1"
                specularExponent="100"
                lightingColor="white"
                result="specLight"
            >
                <fePointLight x="-200" y="-200" z="300" />
            </feSpecularLighting>
            <feComposite
                in="specLight"
                operator="arithmetic"
                k1="0" k2="1" k3="1" k4="0"
                result="litImage"
            />
            <feDisplacementMap
                in="SourceGraphic"
                in2="softMap"
                scale="200"
                xChannelSelector="R"
                yChannelSelector="G"
            />
        </filter>
    </svg>
);

// Core glass surface. Pass `as="a"` + `href` to render an anchor instead of
// a div. `tint` controls the white wash strength — default 0.25 reads nicely
// on Helix's dark canvas; bump it to 0.35–0.45 over photographs.
const LiquidGlass = ({
    children,
    className = '',
    style = {},
    href,
    target = '_self',
    onClick,
    tint = 0.25,
}) => {
    const wrapperStyle = {
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.45), 0 0 32px rgba(24, 181, 240, 0.08)',
        transitionTimingFunction: SPRING_EASE,
        ...style,
    };

    const content = (
        <div
            className={`relative flex font-semibold overflow-hidden text-white cursor-pointer transition-all duration-700 ${className}`}
            style={wrapperStyle}
            onClick={onClick}
        >
            {/* z-0 — backdrop blur + distortion filter */}
            <div
                className="absolute inset-0 z-0 overflow-hidden"
                style={{
                    backdropFilter: 'blur(3px)',
                    WebkitBackdropFilter: 'blur(3px)',
                    filter: 'url(#helix-glass-distortion)',
                    isolation: 'isolate',
                    borderRadius: 'inherit',
                }}
            />
            {/* z-10 — frosted wash */}
            <div
                className="absolute inset-0 z-10"
                style={{
                    background: `rgba(255, 255, 255, ${tint})`,
                    borderRadius: 'inherit',
                }}
            />
            {/* z-20 — inner highlight (top-left light catch) */}
            <div
                className="absolute inset-0 z-20 overflow-hidden"
                style={{
                    boxShadow:
                        'inset 2px 2px 1px 0 rgba(255, 255, 255, 0.35), inset -1px -1px 1px 1px rgba(255, 255, 255, 0.18)',
                    borderRadius: 'inherit',
                }}
            />
            {/* z-30 — content */}
            <div className="relative z-30 w-full">{children}</div>
        </div>
    );

    if (href) {
        const isExternal = /^https?:/i.test(href);
        return (
            <a
                href={href}
                target={isExternal ? target : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="block"
            >
                {content}
            </a>
        );
    }
    return content;
};

// Pre-styled button variant — the most common usage. Spring-grows on hover
// and slightly compresses the inner content for that "depressing into the
// surface" feel.
export const LiquidGlassButton = ({ children, href, onClick, className = '', target }) => (
    <LiquidGlass
        href={href}
        onClick={onClick}
        target={target}
        className={`rounded-2xl px-7 py-4 hover:px-8 hover:py-[18px] hover:rounded-3xl ${className}`}
    >
        <div
            className="transition-all duration-700 hover:scale-[0.97] flex items-center justify-center gap-2"
            style={{ transitionTimingFunction: SPRING_EASE }}
        >
            {children}
        </div>
    </LiquidGlass>
);

export default LiquidGlass;
